/**
 * Данные иллюстраций главной страницы: небольшая квартира, которую hero рисует в 2D
 * и поднимает в 3D, и одна развёртка стены.
 *
 * Квартира та же, что на рендере `public/landing/hero-3d.webp`: её собирали в планировщике
 * из этих же размеров (начало координат плана — точка (35, 45) рисунка), обставляли
 * моделями каталога и снимали 3D-вид. Поменяли план здесь — нужно переснять рендер.
 *
 * Это картинка, а не модель планировщика: движок плана сюда не тянем, чтобы главная
 * грузилась мгновенно. Координаты — в условных единицах viewBox, одна единица = 20 мм,
 * поэтому размеры и площади на рисунке честно считаются из тех же чисел.
 */

/** Прямоугольник в единицах viewBox. */
export interface Box {
	x: number;
	y: number;
	w: number;
	h: number;
}

/** Помещение: чистый контур (между гранями стен), подпись и тон пола. */
export interface DemoRoom extends Box {
	name: string;
	floor: "wood" | "tile";
}

/** Сколько миллиметров в одной единице рисунка. */
export const MM_PER_UNIT = 20;

/** Рамка рисунка плана. */
export const PLAN_VIEWBOX = { w: 600, h: 400 };

/** Стены: наружные толщиной 250 мм, перегородки 120 мм. Разрывы — дверные проёмы и арка. */
export const PLAN_WALLS: Box[] = [
	// наружные
	{ x: 28.75, y: 38.75, w: 542.5, h: 12.5 },
	{ x: 28.75, y: 358.75, w: 233.75, h: 12.5 },
	{ x: 307.5, y: 358.75, w: 263.75, h: 12.5 },
	{ x: 28.75, y: 51.25, w: 12.5, h: 307.5 },
	{ x: 558.75, y: 51.25, w: 12.5, h: 307.5 },
	// между спальней, кабинетом и гостиной
	{ x: 247, y: 51.25, w: 6, h: 111.25 },
	{ x: 247, y: 202.5, w: 6, h: 77.5 },
	{ x: 247, y: 320, w: 6, h: 38.75 },
	{ x: 41.25, y: 207, w: 205.75, h: 6 },
	// кухня и санузел: арка в кухню, дверь в санузел
	{ x: 253, y: 152, w: 47, h: 6 },
	{ x: 380, y: 152, w: 130, h: 6 },
	{ x: 550, y: 152, w: 8.75, h: 6 },
	{ x: 417, y: 51.25, w: 6, h: 100.75 },
];

/** Окна поверх наружных стен. */
export const PLAN_WINDOWS: Box[] = [
	{ x: 94, y: 38.75, w: 100, h: 12.5 },
	{ x: 377.5, y: 38.75, w: 35, h: 12.5 },
	{ x: 441.5, y: 38.75, w: 45, h: 12.5 },
	{ x: 28.75, y: 250, w: 12.5, h: 80 },
	{ x: 558.75, y: 210, w: 12.5, h: 90 },
	{ x: 330, y: 358.75, w: 100, h: 12.5 },
];

/** Двери: полотно и дуга открывания одной линией. */
export const PLAN_DOORS: string[] = [
	"M247 162.5H207A40 40 0 0 0 247 202.5",
	"M253 320H293A40 40 0 0 0 253 280",
	"M550 158V198A40 40 0 0 1 510 158",
	"M307.5 358.75V313.75A45 45 0 0 0 262.5 358.75",
];

export const PLAN_ROOMS: DemoRoom[] = [
	{ name: "Спальня", x: 41.25, y: 51.25, w: 205.75, h: 155.75, floor: "wood" },
	{ name: "Кабинет", x: 41.25, y: 213, w: 205.75, h: 145.75, floor: "wood" },
	{ name: "Кухня", x: 253, y: 51.25, w: 164, h: 100.75, floor: "wood" },
	{ name: "Санузел", x: 423, y: 51.25, w: 135.75, h: 100.75, floor: "tile" },
	{ name: "Гостиная", x: 253, y: 158, w: 305.75, h: 200.75, floor: "wood" },
];

/**
 * Мебель — тонкой линией, как на рабочем чертеже. Места и габариты — те же, что у моделей
 * каталога на рендере `public/landing/hero-3d.webp`, поэтому план и 3D совпадают.
 */
export const PLAN_FURNITURE: string[] = [
	// rug-rect
	"M69 82.5h150v100h-150z",
	// bed-scandi
	"M94.75 51.25h98.5v105h-98.5zM94.75 82.75h98.5M100.75 55.25h39.25a2 2 0 0 1 2 2v14.9a2 2 0 0 1 -2 2h-39.25a2 2 0 0 1 -2 -2v-14.9a2 2 0 0 1 2 -2zM148 55.25h39.25a2 2 0 0 1 2 2v14.9a2 2 0 0 1 -2 2h-39.25a2 2 0 0 1 -2 -2v-14.9a2 2 0 0 1 2 -2z",
	// nightstand
	"M67.75 51.5h25v20h-25z",
	// nightstand
	"M195.25 51.5h25v20h-25z",
	// dresser
	"M120.75 183h46.5v24h-46.5z",
	// plant-ficus
	"M52.88 184a13.12 13.12 0 1 0 26.25 0a13.12 13.12 0 1 0 -26.25 0M59.44 184a6.56 6.56 0 1 0 13.12 0a6.56 6.56 0 1 0 -13.12 0",
	// desk-oak
	"M41.25 262.5h30v55h-30z",
	// chair-wood
	"M73.5 276h28v28h-28z",
	// shelf-cube
	"M120 213h60v19.5h-60z",
	// armchair-papasan
	"M191.75 327.5a23.25 23.25 0 1 0 46.5 0a23.25 23.25 0 1 0 -46.5 0M199.89 327.5a15.11 15.11 0 1 0 30.23 0a15.11 15.11 0 1 0 -30.23 0",
	// floor-lamp-tripod
	"M160.85 340a9.15 9.15 0 1 0 18.3 0a9.15 9.15 0 1 0 -18.3 0",
	// plant-palm
	"M53.74 335a21.26 21.26 0 1 0 42.52 0a21.26 21.26 0 1 0 -42.52 0M64.37 335a10.63 10.63 0 1 0 21.26 0a10.63 10.63 0 1 0 -21.26 0",
	// kitchen-set
	"M253 51.25h120v30h-120z",
	// table-marble
	"M328 117.5a27 27 0 1 0 54 0a27 27 0 1 0 -54 0",
	// chair-wood
	"M303.5 103.5h28v28h-28z",
	// chair-wood
	"M378.5 103.5h28v28h-28z",
	// table-oak
	"M295 212.5h70v40h-70z",
	// chair-soft
	"M300.5 185.75h24v28.5h-24z",
	// chair-soft
	"M335.5 185.75h24v28.5h-24z",
	// chair-soft
	"M300.5 250.75h24v28.5h-24z",
	// chair-soft
	"M335.5 250.75h24v28.5h-24z",
	// rug-round
	"M405 257.5a50 50 0 1 0 100 0a50 50 0 1 0 -100 0",
	// tv-stand-walnut
	"M415 158h80v20h-80z",
	// coffee-table-nesting
	"M430.4 257.5a17.1 17.1 0 1 0 34.2 0a17.1 17.1 0 1 0 -34.2 0M451.7 261.3a13.3 13.3 0 1 0 26.6 0a13.3 13.3 0 1 0 -26.6 0",
	// sofa-rounded
	"M413.5 310.25h83a6 6 0 0 1 6 6v36.5a6 6 0 0 1 -6 6h-83a6 6 0 0 1 -6 -6v-36.5a6 6 0 0 1 6 -6zM411.5 344.2h87",
	// sofa-compact: спинкой к окну, лицом к столику
	"M517.5 215h31a4 4 0 0 1 4 4v77a4 4 0 0 1 -4 4h-31a4 4 0 0 1 -4 -4v-77a4 4 0 0 1 4 -4zM541 219v77",
	// plant-olive
	"M518.5 335a14 14 0 1 0 28 0a14 14 0 1 0 -28 0M525.5 335a7 7 0 1 0 14 0a7 7 0 1 0 -14 0",
	// bath-freestanding
	"M455.23 59.25h17.55a30.23 30.23 0 0 1 30.23 30.23v6.05a30.23 30.23 0 0 1 -30.23 30.23h-17.55a30.23 30.23 0 0 1 -30.23 -30.23v-6.05a30.23 30.23 0 0 1 30.23 -30.23zM455.59 63.25h16.82a26.59 26.59 0 0 1 26.59 26.59v5.32a26.59 26.59 0 0 1 -26.59 26.59h-16.82a26.59 26.59 0 0 1 -26.59 -26.59v-5.32a26.59 26.59 0 0 1 26.59 -26.59z",
	// toilet
	"M526.25 58h32.5v19h-32.5z",
	// vanity-vessel
	"M532.88 82.5h25.75v30h-25.75z",
];

/** Розетки гостиной: точка на грани стены и куда смотрит. */
export const PLAN_SOCKETS = [
	{ x: 253, y: 245, rotate: 90 },
	{ x: 455, y: 158, rotate: 180 },
	{ x: 558.75, y: 325, rotate: -90 },
];

/** Кабель по потолку — только прямые углы, от розетки к розетке. */
export const PLAN_CABLE = "M259 245H290V170H455V164M455 170H540V325H552";

/** Площадь помещения в м² из чистых размеров. */
export const roomArea = (room: Box) => (room.w * MM_PER_UNIT * room.h * MM_PER_UNIT) / 1_000_000;

/** «21,7» — площадь с одним знаком, по-русски. */
export const formatArea = (m2: number) => m2.toFixed(1).replace(".", ",");

/** «10 800» — длина в миллиметрах с неразрывным пробелом между разрядами. */
export const formatMm = (units: number) => (units * MM_PER_UNIT).toLocaleString("ru-RU");

/** Общая площадь квартиры: сумма помещений. */
export const totalArea = () => PLAN_ROOMS.reduce((sum, room) => sum + roomArea(room), 0);

/** Габарит плана по наружным граням стен. */
export const planBounds = (): Box => {
	const left = Math.min(...PLAN_WALLS.map((wall) => wall.x));
	const top = Math.min(...PLAN_WALLS.map((wall) => wall.y));
	const right = Math.max(...PLAN_WALLS.map((wall) => wall.x + wall.w));
	const bottom = Math.max(...PLAN_WALLS.map((wall) => wall.y + wall.h));
	return { x: left, y: top, w: right - left, h: bottom - top };
};

/** Сколько срезов стены поднимает hero в 3D: каждый — копия слоя стен выше предыдущей. */
export const WALL_SLICES = 14;

/** Проём или дверь на развёртке: от левого угла, мм. */
export interface ElevationOpening {
	from: number;
	to: number;
	height: number;
	kind: "arch" | "door";
}

/** Прибор на развёртке: расстояние от левого угла и высота установки, мм. */
export interface ElevationPoint {
	at: number;
	height: number;
	kind: "socket" | "switch";
}

/**
 * Развёртка стены гостиной, общей с кухней и санузлом, — та же стена, что на плане hero:
 * ширина в чистоте, арка в кухню, дверь в санузел, розетка и выключатель.
 */
export const ELEVATION = {
	title: "Гостиная · стена Б",
	width: PLAN_ROOMS[4].w * MM_PER_UNIT,
	height: 2_700,
	openings: [
		{ from: 940, to: 2_540, height: 2_100, kind: "arch" },
		{ from: 5_140, to: 5_940, height: 2_000, kind: "door" },
	] satisfies ElevationOpening[],
	points: [
		{ at: 4_040, height: 300, kind: "socket" },
		{ at: 4_940, height: 900, kind: "switch" },
	] satisfies ElevationPoint[],
};

/** Отметки размерной цепочки вдоль развёртки: углы, края проёмов и приборы. */
export const elevationChain = () => {
	const marks = new Set<number>([0, ELEVATION.width]);
	for (const opening of ELEVATION.openings) {
		marks.add(opening.from);
		marks.add(opening.to);
	}
	for (const point of ELEVATION.points) marks.add(point.at);
	return [...marks].toSorted((a, b) => a - b);
};

/** Отметки цепочки по высоте: пол, приборы, верх проёмов, потолок. */
export const elevationHeights = () => {
	const marks = new Set<number>([0, ELEVATION.height]);
	for (const opening of ELEVATION.openings) marks.add(opening.height);
	for (const point of ELEVATION.points) marks.add(point.height);
	return [...marks].toSorted((a, b) => a - b);
};
